import isNil from 'lodash/isNil';
import { parse, unparse, ParseResult } from 'papaparse';
import { AssetClass } from '../../types';

export const generateCorrelationMatrixCsv = (
  assetClasses: AssetClass[],
  assetCorrelations?: number[][],
) => {
  const assetClassNames = assetClasses
    .filter(({ included }) => included)
    .map(({ customName }) => customName);

  return unparse([
    ['', ...assetClassNames],
    ...assetClassNames.map((assetClassName, i) => [
      assetClassName,
      ...(assetCorrelations ? assetCorrelations[i] : assetClassNames.map(() => '')),
    ]),
  ]);
};

export const parseCorrelationMatrixCsv = (
  correlationMatrixCsv: File,
  assetClasses: AssetClass[],
) => {
  return new Promise<ParseResult<any>>((resolve, reject) => {
    if (!/\.csv$/.test(correlationMatrixCsv.name)) {
      reject(['Please upload a CSV file']);
      return;
    }

    const assetClassNames = assetClasses.reduce(
      (acc, { id, customName }) => ({ ...acc, [customName.toLowerCase()]: id }),
      {},
    );

    parse(correlationMatrixCsv, {
      skipEmptyLines: true,
      complete: ({ data, errors }: ParseResult<any>) => {
        if (errors.length > 0) {
          reject(['Unable to parse the CSV, please make sure it has the correct format']);
          return;
        }

        if (data.length === 0 || data[0].length === 0 || data.length !== data[0].length) {
          reject(['Please check the dimensions of your matrix are the correct size']);
          return;
        }

        const parsingErrors: string[] = [];

        data.forEach((row, i, [firstRow]) => {
          if (i >= 1) {
            if (row[0].trim().toLowerCase() !== firstRow[i].trim().toLowerCase()) {
              const errorMessage = `Please check the row names and column names are in the correct order and match the CSV template`;

              if (parsingErrors.indexOf(errorMessage) < 0) {
                parsingErrors.push(errorMessage);
              }
            }
          }
        });

        if (parsingErrors.length > 0) {
          reject(parsingErrors);
          return;
        }

        const [firstRow, ...rows] = data;

        const parsedMatrix = rows.reduce((acc, [rowAssetClassName, ...row]) => {
          const trimmedRowAssetClassName = (rowAssetClassName || '').trim();

          if (assetClassNames[trimmedRowAssetClassName.toLowerCase()] === undefined) {
            const errorMessage = `The matrix must contain values for each asset class`;

            if (parsingErrors.indexOf(errorMessage) < 0) {
              parsingErrors.push(errorMessage);
            }
          }

          return {
            ...acc,
            [assetClassNames[trimmedRowAssetClassName.toLowerCase()]]: row.reduce(
              (acc2, correlation, i) => {
                const assetClassName = (firstRow[i + 1] || '').trim();

                if (assetClassNames[assetClassName.toLowerCase()] === undefined) {
                  const errorMessage = `The matrix must contain values for each asset class`;

                  if (parsingErrors.indexOf(errorMessage) < 0) {
                    parsingErrors.push(errorMessage);
                  }
                }

                if (isNil(correlation) || correlation === '') {
                  const errorMessage = `The matrix must contain values for each asset class`;

                  if (parsingErrors.indexOf(errorMessage) < 0) {
                    parsingErrors.push(errorMessage);
                  }
                }

                return {
                  ...acc2,
                  [assetClassNames[assetClassName.toLowerCase()]]: parseFloat(correlation),
                };
              },
              {},
            ),
          };
        }, {});

        if (parsingErrors.length > 0) {
          reject(parsingErrors);
        } else {
          resolve(parsedMatrix);
        }
      },
      error: reject,
    });
  });
};
